import { cloneElement, CSSProperties, FC, PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { motion, MotionValue, useAnimation, useMotionValue, useTransform } from 'framer-motion';
import { Typography, useTheme } from '@mui/material';


function getSwipeDirection(x: number, y: number): 'left' | 'right' | 'up' | 'down' | undefined {
  if (x === 0 && y === 0) return undefined;
  const angle = Math.atan2(y, x) * (180 / Math.PI); // [-180, +180] degrees

  if (angle >= -60 && angle <= 60)    return 'right';   // rightward, with some up/down tolerance
  if (angle > 60 && angle < 120)      return 'down';    // mostly down
  if (angle >= 120 || angle <= -120)  return 'left';    // leftward, with some up/down tolerance
  if (angle > -120 && angle < -60)    return 'up';      // mostly up
  return undefined; // Never reached, but for completeness
}

interface Props {
  id?: number | string;
  isFront?: boolean;
  onSwiped?: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  leftLabel?: ReactNode;
  rightLabel?: ReactNode;
  upLabel?: ReactNode;
  downLabel?: ReactNode;
  leftLabelStyles?: CSSProperties;
  rightLabelStyles?: CSSProperties;
  upLabelStyles?: CSSProperties;
  downLabelStyles?: CSSProperties;
  shouldShowHint?: boolean;
}
export const SwipableCard: FC<PropsWithChildren<Props>> = ({ 
  children,
  isFront,
  onSwiped,
  onSwipeLeft, 
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  leftLabel,
  rightLabel,
  upLabel,
  downLabel,
  leftLabelStyles,
  rightLabelStyles,
  upLabelStyles,
  downLabelStyles,
  shouldShowHint = false,
}) => {
  const theme = useTheme();
  const [swipable, setSwipable] = useState(true);
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [dominantDirection, setDominantDirection] = useState<'left' | 'right' | 'up' | 'down' | undefined>(undefined);

  const rotation = useTransform(x, [-300, 0, 300], [20, 0, -20]);

  const animControls = useAnimation();

  useEffect(() => {
    if (!shouldShowHint || !isFront) return;

    const runHintSequence = async () => {
      if (isUserInteracting) return;

      try {
        // Right swipe hint
        await animControls.start({
          x: 120,
          y: 0,
          rotate: 10,
          transition: { duration: 0.6, ease: 'easeOut' },
        });
        
        if (isUserInteracting) return;
        await new Promise(resolve => setTimeout(resolve, 400));
        
        // Return to center
        await animControls.start({
          x: 0,
          y: 0,
          rotate: 0,
          transition: { duration: 0.4, ease: 'easeOut' },
        });
        
        if (isUserInteracting) return;
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Left swipe hint
        await animControls.start({
          x: -120,
          y: 0,
          rotate: -10,
          transition: { duration: 0.6, ease: 'easeOut' },
        });
        
        if (isUserInteracting) return;
        await new Promise(resolve => setTimeout(resolve, 400));
        
        // Return to center
        await animControls.start({
          x: 0,
          y: 0,
          rotate: 0,
          transition: { duration: 0.4, ease: 'easeOut' },
        });
        
        if (isUserInteracting) return;
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Up swipe hint
        await animControls.start({
          x: 0,
          y: -80,
          rotate: -5,
          transition: { duration: 0.6, ease: 'easeOut' },
        });
        
        if (isUserInteracting) return;
        await new Promise(resolve => setTimeout(resolve, 400));
        
        // Return to center
        await animControls.start({
          x: 0,
          y: 0,
          rotate: 0,
          transition: { duration: 0.4, ease: 'easeOut' },
        });
        
        if (isUserInteracting) return;
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Down swipe hint
        await animControls.start({
          x: 0,
          y: 80,
          rotate: 5,
          transition: { duration: 0.6, ease: 'easeOut' },
        });
        
        if (isUserInteracting) return;
        await new Promise(resolve => setTimeout(resolve, 400));
        
        // Return to center
        await animControls.start({
          x: 0,
          y: 0,
          rotate: 0,
          transition: { duration: 0.4, ease: 'easeOut' },
        });

      } catch (error) {
        console.error(error);
        // Animation was interrupted, reset to center
        animControls.set({ x: 0, y: 0, rotate: 0 });
      }
    };

    // Initial delay before first hint
    const initialTimeout = setTimeout(runHintSequence, 2000);
    
    // Repeat every 20 seconds
    const interval = setInterval(() => {
      runHintSequence();
    }, 20000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [shouldShowHint, isFront, animControls, isUserInteracting]);

  const handleDragStart = useCallback(() => {
    setIsUserInteracting(true);
    animControls.stop();
  }, [animControls]);

  const handleDragEnd = useCallback(async (event: any, info: any) => {
    if (!swipable) return;

    const thresholdX = window.innerWidth * 0.4;
    const thresholdY = window.innerHeight * 0.2;

    // Prefer info.offset but fall back to motion values if needed
    const rawOffset = info?.offset ?? { x: 0, y: 0 };
    let dx = typeof rawOffset.x === 'number' ? rawOffset.x : 0;
    let dy = typeof rawOffset.y === 'number' ? rawOffset.y : 0;

    if ((dx === 0 && dy === 0)) {
      // Fallback to current transform values if offsets are unreliable/zero
      dx = x.get();
      dy = y.get();
    }

    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    // passed threshold?
    if (absX > thresholdX || absY > thresholdY) {
      setSwipable(false);

      // Resolve direction robustly with fallback to dominant axis
      let direction = getSwipeDirection(dx, dy);
      if (!direction) {
        direction = absX >= absY ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
      }

      // Final destination
      const outMultiplier = 2;
      let finalX = 0, finalY = 0, finalRotate = 0;

      switch (direction) {
      case 'left':
        finalX = -window.innerWidth * outMultiplier;
        finalY = dy * outMultiplier;
        finalRotate = -15; break;
      case 'right':
        finalX = window.innerWidth * outMultiplier;
        finalY = dy * outMultiplier;
        finalRotate = 15; break;
      case 'up':
        finalY = -window.innerHeight * outMultiplier;
        finalX = dx * outMultiplier;
        finalRotate = -8; break;
      case 'down':
        finalY = window.innerHeight * outMultiplier;
        finalX = dx * outMultiplier;
        finalRotate = 8; break;
      default:
        break;
      }

      // Call the correct callback FIRST to avoid race conditions with animation
      switch (direction) {
      case 'left': onSwipeLeft?.(); break;
      case 'right': onSwipeRight?.(); break;
      case 'up': onSwipeUp?.(); break;
      case 'down': onSwipeDown?.(); break;
      default: break;
      }
      onSwiped?.();

      // Fire-and-forget animation (no await)
      animControls.start({
        x: finalX,
        y: finalY,
        rotate: finalRotate,
        opacity: 0,
      }).catch(() => {
        // ignore animation errors after unmount/interrupt
      });
    } else {
      // Snap back and allow hints to resume after a delay
      await animControls.start({ x: 0, y: 0, rotate: 0, opacity: 1 });
      setTimeout(() => setIsUserInteracting(false), 2000);
    }
  }, [swipable, animControls, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onSwiped, x, y]);

  useEffect(() => {
    const handler = () => {
      const latestX = x.get();
      const latestY = y.get();
      setDominantDirection(getSwipeDirection(latestX, latestY));
    };
    const unsubX = x.on('change', handler);
    const unsubY = y.on('change', handler);
    return () => { unsubX(); unsubY(); };
  }, [x, y]);

  return (
    <motion.div
      drag
      animate={animControls}
      dragConstraints={{ left: -1000, right: 1000, top: -1000, bottom: 1000 }}
      style={{
        x,
        y,
        width: '100%',
        height: '100%',
        rotate: rotation,
        position: 'relative',
        transformOrigin: 'top center',
        borderRadius: 8,
        willChange: 'transform',
      }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <motion.div
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        {cloneElement(children as React.ReactElement<any>, {
          onSwiped,
          isFront,
        })}
      </motion.div>
      {/* Overlays */}
      {dominantDirection === 'left'  && (
        <SwipeOverlayLabel
          position="left"
          background={`linear-gradient(to bottom, ${theme.palette.error.main}, ${theme.palette.error.dark})`}
          label={leftLabel}
          x={x}
          y={y}
          style={leftLabelStyles}
        />
      )}
      {dominantDirection === 'right'  && (
        <SwipeOverlayLabel
          position="right"
          background={`linear-gradient(to bottom, ${theme.palette.success.main}, ${theme.palette.success.dark})`}
          label={rightLabel}
          x={x}
          y={y}
          style={rightLabelStyles}
        />
      )}
      {dominantDirection === 'up'  && (
        <SwipeOverlayLabel
          position="up"
          background={`linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.info.dark})`}
          label={upLabel}
          x={x}
          y={y}
          style={upLabelStyles}
        />
      )}
      {dominantDirection === 'down'  && (
        <SwipeOverlayLabel
          position="down"
          background={`linear-gradient(to left, ${theme.palette.fourtiary.main}, ${theme.palette.fourtiary.dark})`}
          label={downLabel}
          x={x}
          y={y}
          style={downLabelStyles}
        />
      )}
    </motion.div>
  );
};

interface OverlayProps {
  position: 'left' | 'right' | 'up' | 'down',
  background: string,
  label: React.ReactNode,
  x: MotionValue<number>;
  y: MotionValue<number>;
  style?: CSSProperties;
}

const SwipeOverlayLabel: React.FC<OverlayProps> = ({
  position,
  background,
  label,
  x,
  y,
  style = {},
}) => {

  const theme = useTheme();

  const labelPos = useMemo(() => {
    // Give each label a characteristic rotation
    switch (position) {
    case 'left': return { right: 100, top: 50 };
    case 'right': return { left: 100, top: 50 };
    case 'up': return { left: 0, right: 0, bottom: 200 };
    case 'down': return { left: 0, right: 0, top: 200 };
    default: return 0;
    }
  }, [position]);

  const rotation = useMemo(() => {
    // Give each label a characteristic rotation
    switch (position) {
    case 'left': return -16;
    case 'right': return 16;
    case 'up': return -8;
    case 'down': return 8;
    default: return 0;
    }
  }, [position]);

  const horizontalTranslationX = useTransform(x, [-200, 0, 200], [80, 0, -80]);
  const horizontalTranslationY = useTransform(x, [-200, 0, 200], [-32, 0, -32]);
  const verticalTranslationY = useTransform(y, [-200, 0, 200], [100, 0, -100]);

  const horizontalOpacity = useTransform(x, [-200, -100, -50, 0, 50, 100, 200], [1, 0.8, 0.6, 0, 0.6, 0.8, 1]);
  const verticalOpacity = useTransform(y, [-200, -100, -50, 0, 50, 100, 200], [1, 0.8, 0.6, 0, 0.6, 0.8, 1]);


  const opacity = useMemo(() => {
    // Give each label a characteristic rotation
    switch (position) {
    case 'left':
    case 'right':
      return horizontalOpacity;
    case 'up':
    case 'down':
      return verticalOpacity;
    default: return 0;
    }
  }, [position, horizontalOpacity, verticalOpacity]);

  const color = useMemo(() => {
    // Give each label a characteristic rotation
    switch (position) {
    case 'left': return theme.palette.error.light;
    case 'right': return theme.palette.success.light;
    case 'up': return theme.palette.info.light;
    case 'down': return theme.palette.fourtiary.light;
    default: return '#fff';
    }
  }, [position, theme]);

  const motionStyle = useMemo(() => {
    if (position === 'left') return { translateX: horizontalTranslationX, translateY: horizontalTranslationY };
    if (position === 'right') return { translateX: horizontalTranslationX, translateY: horizontalTranslationY };
    if (position === 'up') return { translateY: verticalTranslationY };
    if (position === 'down') return { translateY: verticalTranslationY };
    return {};
  }, [position, horizontalTranslationX, horizontalTranslationY, verticalTranslationY]);
  

  const animate = {
    scale: [0.6, 1.6, 1],
    rotate: rotation,
  };

  return (
    <motion.div
      style={{
        position: 'absolute',
        pointerEvents: 'none',
        color: 'white',
        minWidth: 100,
        textAlign: 'center',
        zIndex: 4,
        background,
        opacity,
        top: 0,
        left: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        willChange: 'opacity, background',
        borderRadius: 16,
      }}
    >
      <motion.div
        style={{
          position: 'absolute',
          willChange: 'transform, top, right, bottom, left',
          ...labelPos,
          ...motionStyle,
        }}
        animate={animate}
        transition={{ duration: 0.3 }}
      >
        <Typography
          fontSize={style.fontSize}
          fontWeight={900}
          textTransform="uppercase"
          letterSpacing={0.2}
          color="white"
          sx={{
            ...style,
            fontFamily: '"Rubik", sans-serif',
            textShadow: `
              2px 2px 0 ${color},
              3px 3px 12px #0008,
              0 1px 8x ${color}80
            `,
            lineHeight: 1.1,
            filter: 'drop-shadow(-5px 8px 0px rgba(0,0,0,1))',
            userSelect: 'none',
            willChange: 'color, textShadow',
          }}
        >
          {label}
        </Typography>
      </motion.div>
    </motion.div>
  );
};
